/* global axios */
import ApiClient from './ApiClient';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;
// Preserve the selected calendar date; the server applies the account timezone.
const calendarDate = timestamp => {
  if (timestamp == null) return undefined;
  const date = new Date(timestamp * 1000);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
  }) {
    return axios.get(`${this.url}`, {
      params: {
        metric,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getDrilldown({
    metric,
    bucketTimestamp,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    page,
    perPage,
    signal,
  }) {
    const requestConfig = {
      params: {
        metric,
        bucket_timestamp: bucketTimestamp,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
        page,
        per_page: perPage,
      },
    };

    if (signal) {
      requestConfig.signal = signal;
    }

    return axios.get(`${this.url}/drilldown`, requestConfig);
  }

  // eslint-disable-next-line default-param-last
  getSummary(since, until, type = 'account', id, groupBy, businessHours) {
    return axios.get(`${this.url}/summary`, {
      params: {
        since,
        until,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getConversationMetric(type = 'account', page = 1) {
    return axios.get(`${this.url}/conversations`, {
      params: {
        type,
        page,
      },
    });
  }

  getAgentReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/agents`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getConversationsSummaryReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/conversations_summary`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getConversationTrafficCSV({ daysBefore = 6 } = {}) {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: { timezone_offset: getTimeOffset(), days_before: daysBefore },
    });
  }

  getLabelReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/labels`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getInboxReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/inboxes`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getTeamReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/teams`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getBotMetrics({ from, to } = {}) {
    return axios.get(`${this.url}/bot_metrics`, {
      params: { since: from, until: to },
    });
  }

  getConversationOrigin({ since, until, inboxId, assigneeId } = {}) {
    return axios.get(`${this.url}/conversation_origin`, {
      params: {
        since_date: calendarDate(since),
        until_date: calendarDate(until),
        inbox_id: inboxId,
        assignee_id: assigneeId,
      },
    });
  }

  getCampaignPerformance({ page, perPage, sinceDate, untilDate } = {}) {
    return axios.get(`${this.url}/campaign_performance`, {
      params: {
        page,
        per_page: perPage,
        since_date: sinceDate,
        until_date: untilDate,
      },
    });
  }

  getCampaignPerformanceDetail({
    id,
    page,
    perPage,
    status,
    reason,
    query,
  } = {}) {
    return axios.get(`${this.url}/campaign_performance_detail`, {
      params: { id, page, per_page: perPage, status, reason, query },
    });
  }

  getCampaignPerformanceExport({ id, status, reason, query } = {}) {
    return axios.get(`${this.url}/campaign_performance_export`, {
      params: { id, status, reason, query },
      responseType: 'blob',
    });
  }

  getAgentLabelMatrix({ since, until, inboxId } = {}) {
    return axios.get(`${this.url}/agent_label_matrix`, {
      params: {
        since_date: calendarDate(since),
        until_date: calendarDate(until),
        inbox_id: inboxId,
      },
    });
  }

  getAgentOriginStats({ since, until, inboxId } = {}) {
    return axios.get(`${this.url}/agent_origin_stats`, {
      params: {
        since_date: calendarDate(since),
        until_date: calendarDate(until),
        inbox_id: inboxId,
      },
    });
  }

  getBotSummary({ from, to, groupBy, businessHours } = {}) {
    return axios.get(`${this.url}/bot_summary`, {
      params: {
        since: from,
        until: to,
        type: 'account',
        group_by: groupBy,
        business_hours: businessHours,
      },
    });
  }
}

export default new ReportsAPI();
